import { useEffect, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Bell, Building2, ChevronDown, Globe, KeyRound, Menu, Search, Siren, UserPen, X } from 'lucide-react'
import { Dropdown } from 'antd'
import { toast } from 'react-toastify'
import { ChangePassword } from 'pages/user'
import { SosModal, GuardSosModal } from 'components/forms/help_desk'
import img_nf from '../../assets/images/default.jpg'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toggleChangePasswordModal, toggleSosModal, toggleGuardSosModal } from '../../redux/slices/globalSlice'
import { premiseByUser, switchPremiseByUser } from '../../redux/slices/premises/premise/Premise'
import { sidebar } from '../../redux/slices/assets'
import { SessionManager, getConfig } from 'utils'
import { useTranslation } from 'react-i18next'

export default function Header(props) {

    const { collapsed, setCollapsed, drawerOpen, setDrawerOpen, isMobileOrTablet } = props
    const [date] = useState(new Date().toDateString())
    const [premiseOpen, setPremiseOpen] = useState(false)
    const [selectedPremise, setSelectedPremise] = useState(null)
    const [selectedUnit, setSelectedUnit] = useState(null)
    const [switching, setSwitching] = useState(false)
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const premiseList = useSelector(state => state?.premise?.byUserList) || []
    const premiseLoading = useSelector(state => state?.premise?.byUserLoading)

    let cookieUser = SessionManager.getCookie("user");
    let user_details = cookieUser ? JSON.parse(cookieUser) : null;

    const defaultPremiseName = user_details?.premiseInfo?.name || 'Select Premise'
    const defaultUnit = user_details?.premiseInfo?.unitInfo?.unit_number || ''
    // Cookie: premiseInfo.unit_id
    const currentUnitId = user_details?.premiseInfo?.unit_id || null
    const extraPremiseCount = Math.max((premiseList?.length || 0) - 1, 0)

    const getUnitId = (unit) => unit?.unit_id || null
    const getPremiseId = (premise) => premise?.premiseInfo?.id || premise?.id || null
    const getPremiseName = (premise) => premise?.premiseInfo?.name || ''
    const getPremiseUnits = (premise) => premise?.premiseInfo?.unitInfo || []

    const lang_option = {
        items: [
            { key: 'en', label: 'English' },
        ],
        selectable: true,
        defaultSelectedKeys: [i18n?.language],
        onClick: ({ key }) => {
            i18n.changeLanguage(key)
        },
    };

    const settings_option = [
        {
            key: '1',
            label: 'Profile',
            icon: <UserPen size={16} />,
            onClick: () => navigate('/profile')
        },
        {
            key: '2',
            label: 'Change Password',
            icon: <KeyRound size={16} />,
            onClick: () => changePasswordModal()
        },
    ];

    const changePasswordModal = () => dispatch(toggleChangePasswordModal())
    const roleCookie = SessionManager.getCookie("role")
    const userRole = roleCookie ? JSON.parse(roleCookie)?.role_name : null
    const openSosModal = () => {
        if (userRole === "Guard") dispatch(toggleGuardSosModal())
        else if (userRole === "Resident") dispatch(toggleSosModal())
    }

    var permission = JSON.parse(SessionManager.getSession('permission'))
    const canAccessSos = permission?.some((val) => val.sub_action_name === RoutesMap?.SOS?.name)

    const openPremiseModal = () => {
        setPremiseOpen(true)
        setSwitching(false)
        dispatch(premiseByUser({ navigate }))
    }

    const closePremiseModal = () => {
        setPremiseOpen(false)
        setSelectedPremise(null)
        setSelectedUnit(null)
        setSwitching(false)
    }

    useEffect(() => {
        if (!premiseOpen || !premiseList.length) return

        const cookiePremiseId = user_details?.premise_id
        const cookieUnitId = currentUnitId

        // Premise: cookie wala default selected
        const currentPremise = premiseList.find(p => String(getPremiseId(p)) === String(cookiePremiseId))
            || null
        setSelectedPremise(currentPremise)

        // Unit: cookie wala default selected (pehli unit pe fallback nahi)
        const units = getPremiseUnits(currentPremise)
        const currentUnit = cookieUnitId != null
            ? units.find(u => String(getUnitId(u)) === String(cookieUnitId)) || null
            : null
        setSelectedUnit(currentUnit)
    }, [premiseOpen, premiseList, user_details?.premise_id, currentUnitId])

    const handleUnitSelect = (premise, unit) => {
        setSelectedPremise(premise)
        setSelectedUnit(unit)
    }

    const selectedPremiseId = getPremiseId(selectedPremise)
    const selectedPremiseName = getPremiseName(selectedPremise)
    const selectedUnitId = getUnitId(selectedUnit)
    const selectedUnitLabel = selectedUnit?.unit_number || ''

    const isDifferentSelection = Boolean(
        selectedPremiseId
        && selectedUnitId
        && (
            String(selectedPremiseId) !== String(user_details?.premise_id)
            || String(selectedUnitId) !== String(currentUnitId)
        )
    )

    const handleSwitchPremise = async () => {
        if (!selectedPremiseId || !selectedUnitId || !isDifferentSelection || switching) return

        setSwitching(true)
        const res = await dispatch(switchPremiseByUser({
            premise_id: selectedPremiseId,
            unit_id: selectedUnitId,
            navigate,
        }))

        if (res?.payload?.status === 200 || res?.payload?.status === 201) {
            const responseData = res?.payload?.data?.data
            const userData = responseData?.user_data
            const roleTokens = responseData?.role_tokens || []
            const currentRole = SessionManager.getCookie('role')
                ? JSON.parse(SessionManager.getCookie('role'))
                : null

            // Clear old auth cookies / session
            SessionManager.removeCookie('user')
            SessionManager.removeCookie('user_token')
            SessionManager.removeCookie('role')
            SessionManager.removeSession('permission')

            // Set new user cookie
            if (userData) {
                SessionManager.setCookie('user', JSON.stringify(userData), 3)
            }

            // Prefer same role as before, else first role from response
            const nextRole = roleTokens.find(r => String(r?.role_id) === String(currentRole?.role_id))
                || roleTokens.find(r => r?.role_name === currentRole?.role_name)
                || roleTokens[0]

            if (nextRole?.access_token) {
                SessionManager.setCookie('user_token', `Bearer ${nextRole.access_token}`, 3)
                SessionManager.setCookie('role', JSON.stringify(nextRole), 3)

                const sidebarRes = await dispatch(sidebar({ navigate }))
                if (sidebarRes?.payload?.status === 200) {
                    const permission = sidebarRes?.payload?.data?.data?.attributes?.map(val => JSON.parse(val)).flat()
                    SessionManager.setSession('permission', JSON.stringify(permission))
                }
            }

            toast.success(res?.payload?.data?.message || 'Premise switched successfully', { position: 'bottom-right' })
            closePremiseModal()
            window.location.href = nextRole?.navigation || RoutesMap.DASHBOARD.path
        } else if (res?.payload?.status === 401) {
            SessionManager.removeCookie('user_token')
            navigate(RoutesMap.LOGIN.path)
        } else {
            setSwitching(false)
            toast.error(res?.payload?.data?.message || 'Something went wrong', { position: 'bottom-right' })
        }
    }

    return (
        <>
            <nav className={`navbar fixed-top ${isMobileOrTablet ? 'navbar_mobile_view' : collapsed ? "navbar_body_collapsed" : "navbar_body_notcollapsed"}`} >
                <div className="container-fluid">
                    <span className="navbar-brand d-flex align-items-center">
                        <Menu size={20} onClick={() => isMobileOrTablet ? setDrawerOpen(!drawerOpen) : setCollapsed(!collapsed)} style={{ cursor: 'pointer' }} />
                        <span className='d-flex flex-column ms-3'>
                            <span style={{ fontSize: '13px' }} className="fw-bold">Welcome, {user_details ? `${user_details.first_name || ""} ${user_details.last_name || ""}` : "Guest"}!</span>
                            <span style={{ fontSize: '11px' }} className='text-secondary'>{date} <i className='teal'>{user_details?.premiseInfo?.name && `- ${user_details?.premiseInfo?.name}` || ""}</i></span>
                        </span>
                    </span>

                    <div className='d-flex align-items-center header-actions'>

                        <div className="header-search">
                            <Search size={18} className="header-search__icon" aria-hidden />
                            <input
                                type="text"
                                className="header-search__input"
                                placeholder=""
                            />
                        </div>

                        {userRole === 'Resident' && (
                            <button type="button" className="header-premise-btn" onClick={openPremiseModal}>
                                <Building2 size={15} />
                                <span className="header-premise-btn__text">
                                    {defaultPremiseName}
                                    {defaultUnit ? ` · ${defaultUnit}` : ''}
                                </span>
                                {extraPremiseCount > 0 && (
                                    <span className="header-premise-btn__count">+{extraPremiseCount}</span>
                                )}
                                <ChevronDown size={14} className="header-premise-btn__chevron" />
                            </button>
                        )}

                        {canAccessSos &&
                            <>
                                <span>|</span>
                                <button
                                    type="button"
                                    className="header-sos-btn"
                                    onClick={openSosModal}
                                    title={t('sos.sos')}
                                >
                                    <Siren size={16} />
                                    {t('sos.sos')}
                                </button>
                                <span>|</span>
                            </>
                        }

                        <Dropdown
                            menu={lang_option}
                            placement="bottom"
                        >
                            <Globe size={18} style={{ cursor: 'pointer' }} />
                        </Dropdown>

                        <span>|</span>

                        <button
                            type="button"
                            className="header-notification-btn"
                            onClick={() => navigate(RoutesMap.NOTIFICATIONS.path)}
                        >
                            <Bell size={18} />
                        </button>

                        <span>|</span>

                        <Dropdown
                            menu={{ items: settings_option }}
                            placement="bottom"
                        >
                            <img
                                src={user_details?.user_profile && getConfig.BASE_URL + user_details?.user_profile || img_nf}
                                alt="admin"
                                className='img-fluid header_profile_img'
                                style={{ cursor: 'pointer' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = img_nf }}
                            />
                        </Dropdown>
                    </div>
                </div>
            </nav>

            {userRole === 'Resident' && premiseOpen && (
                <div className="switch-flat-overlay" onClick={closePremiseModal}>
                    <div className="switch-flat-modal" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="switch-flat-modal__close" onClick={closePremiseModal} aria-label="Close">
                            <X size={18} />
                        </button>

                        <h2 className="switch-flat-modal__title">Switch Premise</h2>
                        <p className="switch-flat-modal__subtitle">
                            One sign-in covers every premise you hold. Select a premise and unit to switch.
                        </p>

                        <div className="switch-flat-modal__body">
                            {premiseLoading ? (
                                <div className="switch-flat-modal__empty">Loading...</div>
                            ) : premiseList.length === 0 ? (
                                <div className="switch-flat-modal__empty">No premises found</div>
                            ) : (
                                <div className="switch-flat-premise-grid">
                                    {premiseList.map((premise) => {
                                        const premiseId = getPremiseId(premise)
                                        const premiseName = getPremiseName(premise)
                                        const units = getPremiseUnits(premise)
                                        const isCurrentPremise = String(premiseId) === String(user_details?.premise_id)
                                        const isSelectedPremise = String(selectedPremiseId) === String(premiseId)

                                        return (
                                            <div
                                                key={premiseId}
                                                className={`switch-flat-premise-box${isCurrentPremise ? ' switch-flat-premise-box--current' : ''}${isSelectedPremise ? ' switch-flat-premise-box--selected' : ''}`}
                                            >
                                                <div className="switch-flat-premise-box__header">
                                                    <div className="switch-flat-premise-box__icon">
                                                        <Building2 size={18} />
                                                    </div>
                                                    <div className="switch-flat-premise-box__title-wrap">
                                                        <span className="switch-flat-premise-box__name">{premiseName}</span>
                                                        {isCurrentPremise && <span className="switch-flat-premise-box__badge">Current</span>}
                                                    </div>
                                                </div>

                                                {units.length > 0 ? (
                                                    <div className="switch-flat-unit-info-list">
                                                        {units.map((unit) => {
                                                            const unitId = getUnitId(unit)
                                                            const unitNumber = unit?.unit_number || '-'
                                                            const zoneName = unit?.zoneInfo?.zone_name || ''
                                                            const levelName = unit?.levelInfo?.level_name || ''
                                                            const unitType = unit?.type || ''
                                                            const isSelectedUnit = isSelectedPremise
                                                                && String(selectedUnitId) === String(unitId)
                                                            const isCurrentUnit = isCurrentPremise
                                                                && String(currentUnitId) === String(unitId)

                                                            return (
                                                                <button
                                                                    key={unitId}
                                                                    type="button"
                                                                    className={`switch-flat-unit-info${isSelectedUnit ? ' switch-flat-unit-info--selected' : ''}${isCurrentUnit ? ' switch-flat-unit-info--current' : ''}`}
                                                                    onClick={() => handleUnitSelect(premise, unit)}
                                                                >
                                                                    <span className="switch-flat-unit-info__number">
                                                                        {unitNumber}{unitType ? ` · ${unitType}` : ''}
                                                                        {isCurrentUnit ? ' · Current' : ''}
                                                                    </span>
                                                                    {(zoneName || levelName) && (
                                                                        <span className="switch-flat-unit-info__meta">
                                                                            {[zoneName, levelName].filter(Boolean).join(' · ')}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            )
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="switch-flat-group__empty">No units assigned</p>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {isDifferentSelection && (
                            <div className="switch-flat-confirm">
                                <p className="switch-flat-confirm__msg">
                                    Do you want to switch to <strong>{selectedPremiseName}</strong>
                                    {selectedUnitLabel ? <> · <strong>{selectedUnitLabel}</strong></> : null}?
                                </p>
                            </div>
                        )}

                        <div className="switch-flat-modal__footer">
                            <button type="button" className="switch-flat-btn switch-flat-btn--cancel" onClick={closePremiseModal}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="switch-flat-btn switch-flat-btn--switch"
                                onClick={handleSwitchPremise}
                                disabled={!isDifferentSelection || switching}
                            >
                                {switching ? 'Switching...' : 'Switch'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ChangePassword />
            {userRole === "Resident" && <SosModal />}
            {userRole === "Guard" && <GuardSosModal />}
        </>
    )
}
